export const MICROCENTS_PER_USD = 100_000_000
/** Same fractional scale as USD microcents: 1 currency unit = 100_000_000. */
export const MICROUNITS_PER_CURRENCY = MICROCENTS_PER_USD

/** Ledger default until UsageRating assigns a real rate_key — not a product tariff. */
export const PLACEHOLDER_TARIFF_CODE = "pilot"

export function formatTariffCode(code: string | null | undefined): string {
  if (!code || code === PLACEHOLDER_TARIFF_CODE) return "—"
  return code
}

export type UsageUnitsInput = {
  total_tokens?: number | null
  billed_duration_sec?: number | null
  video_duration_sec?: number | null
  audio_duration_sec?: number | null
  image_count?: number | null
  text_tokens?: number | null
  voice_chars?: number | null
  spoken_text_length?: number | null
}

/**
 * Деньги всегда в одной локали на валюту: $ — en-US (точка, запятые разрядов),
 * ₽ — ru-RU (запятая, пробелы разрядов). toFixed здесь запрещён — он даёт
 * точку независимо от локали, и «364.60 ₽» рядом с «11 303,40 ₽» разъезжаются.
 */
function formatMoneyNumber(value: number, locale: string, digits: number): string {
  return value.toLocaleString(locale, { maximumFractionDigits: digits, minimumFractionDigits: digits })
}

/** Мелкие суммы (цена секунды, токена) показываем с запасом знаков, чтобы не схлопнуть в ноль. */
function moneyFractionDigits(value: number, digits: number): number {
  const abs = Math.abs(value)
  return value === 0 || abs >= 1 ? digits : abs >= 0.01 ? Math.max(digits, 4) : 6
}

/** Символ впереди, знак — перед символом: «-$5.00», а не «$-5.00». */
function formatPrefixedMoney(symbol: string, value: number, digits: number): string {
  const sign = value < 0 ? "-" : ""
  return `${sign}${symbol}${formatMoneyNumber(Math.abs(value), "en-US", moneyFractionDigits(value, digits))}`
}

export function formatUsdFromMicrocents(microcents: number | null | undefined, digits = 2): string {
  if (microcents == null) return "—"
  return formatPrefixedMoney("$", microcents / MICROCENTS_PER_USD, digits)
}

/** Sell-side GPL amounts stored in the same micro-unit scale as cost microcents. */
export function formatRubFromMicrounits(microunits: number | null | undefined, digits = 2): string {
  if (microunits == null) return "—"
  const rub = microunits / MICROUNITS_PER_CURRENCY
  return `${formatMoneyNumber(rub, "ru-RU", moneyFractionDigits(rub, digits))} ₽`
}

/** Евро — в той же записи, что доллар: знак впереди, en-US разряды. */
export function formatEurFromMicrounits(microunits: number | null | undefined, digits = 2): string {
  if (microunits == null) return "—"
  return formatPrefixedMoney("€", microunits / MICROUNITS_PER_CURRENCY, digits)
}

/** Знаковая дельта проводки: явный плюс у положительных сумм. */
export function formatSignedRubFromMicrounits(microunits: number | null | undefined, digits = 2): string {
  return formatSignedMoneyFromMicrounits(microunits, "RUB", digits)
}

export function formatSignedMoneyFromMicrounits(
  microunits: number | null | undefined,
  currency: string | null | undefined,
  digits = 2,
): string {
  if (microunits == null) return "—"
  const formatted = formatMoneyFromMicrounits(microunits, currency, digits)
  return microunits > 0 ? `+${formatted}` : formatted
}

export type AccountMoney = {
  currency?: string | null
  balance_microcents: number
  held_microcents: number
  available_microcents: number
}

export type AccountTotals = { currency: string; balance: number; held: number; available: number }

/** Итоги счетов по валютам — по строке на валюту, в порядке первого появления. */
export function accountTotalsByCurrency(accounts: AccountMoney[]): AccountTotals[] {
  const totals = new Map<string, AccountTotals>()
  for (const account of accounts) {
    const currency = (account.currency || DEFAULT_BILLING_CURRENCY).toUpperCase()
    const row = totals.get(currency) ?? { currency, balance: 0, held: 0, available: 0 }
    row.balance += account.balance_microcents
    row.held += account.held_microcents
    row.available += account.available_microcents
    totals.set(currency, row)
  }
  return Array.from(totals.values())
}

/** Сумма без копеек — для узких мест вроде меню; точное значение живёт в подсказке. */
export function formatRubWhole(microunits: number | null | undefined): string {
  return formatMoneyWhole(microunits, "RUB")
}

export function formatMoneyWhole(microunits: number | null | undefined, currency: string | null | undefined): string {
  if (microunits == null) return "—"
  // `|| 0` убирает минус-ноль: Intl показал бы «-0 ₽».
  const rounded = Math.round(microunits / MICROUNITS_PER_CURRENCY) || 0
  return formatMoneyFromMicrounits(rounded * MICROUNITS_PER_CURRENCY, currency, 0)
}

/** Календарная дата «сегодня» в московском часовом поясе (YYYY-MM-DD). */
export function moscowToday(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Moscow" }).format(new Date())
}

/**
 * Календарных дней от «сегодня МСК» до ISO-даты: 0 — истекает сегодня,
 * отрицательное — дата уже в прошлом. Планы живут по московскому календарю.
 */
export function moscowDaysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null
  const toUtcMidnight = (value: string): number | null => {
    const [year, month, day] = value.slice(0, 10).split("-").map(Number)
    if (!year || !month || !day) return null
    return Date.UTC(year, month - 1, day)
  }
  const target = toUtcMidnight(iso)
  const today = toUtcMidnight(moscowToday())
  if (target == null || today == null) return null
  return Math.round((target - today) / 86_400_000)
}

/** Дата-время в МСК — журнал проводок живёт по московскому времени. */
export function formatMoscowDateTime(iso: string | null | undefined, locale = "en"): string {
  if (!iso) return "—"
  try {
    return new Date(iso).toLocaleString(locale === "ru" ? "ru-RU" : "en-US", {
      timeZone: "Europe/Moscow",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  } catch {
    return iso
  }
}

/** Валюты продажной стороны (docs/billing_currency_accounts.md); RUB — основная. */
export const BILLING_CURRENCIES = ["RUB", "USD", "EUR"] as const
export type BillingCurrency = (typeof BILLING_CURRENCIES)[number]
export const DEFAULT_BILLING_CURRENCY: BillingCurrency = "RUB"

/** Без валюты — USD: так исторически приходят суммы себестоимости и провайдеров. */
export function formatMoneyFromMicrounits(
  microunits: number | null | undefined,
  currency: string | null | undefined,
  digits = 2,
): string {
  switch ((currency || "USD").toUpperCase()) {
    case "RUB":
      return formatRubFromMicrounits(microunits, digits)
    case "EUR":
      return formatEurFromMicrounits(microunits, digits)
    default:
      return formatUsdFromMicrocents(microunits, digits)
  }
}

export function currencySymbol(currency: string | null | undefined): string {
  switch ((currency || DEFAULT_BILLING_CURRENCY).toUpperCase()) {
    case "USD":
      return "$"
    case "EUR":
      return "€"
    default:
      return "₽"
  }
}

/** Компактная сумма для осей графиков: value — уже в целых единицах валюты. */
export function formatCompactMoney(value: number, currency: string | null | undefined, locale = "en"): string {
  const symbol = currencySymbol(currency)
  // Как у полных сумм: рубли — в локали интерфейса, $/€ — в en-US («$0.8», не «$0,8»).
  if (symbol === "₽") return `${formatCompactNumber(value, locale)} ₽`
  return `${symbol}${formatCompactNumber(value, "en")}`
}

/** KPI-сумма в любой валюте: без копеек от шести знаков целой части (см. formatRubKpi). */
export function formatMoneyKpi(microunits: number | null | undefined, currency: string | null | undefined): string {
  if (microunits == null) return "—"
  const value = microunits / MICROUNITS_PER_CURRENCY
  return formatMoneyFromMicrounits(microunits, currency, Math.abs(value) >= 100_000 ? 0 : 2)
}

/**
 * Деньги для KPI-плиток: полная сумма с группировкой разрядов, но без копеек,
 * когда целая часть шестизначная и больше — на десятках миллионов копейки
 * только съедают место. Точное значение с копейками — рядом, в title-тултипе.
 */
export function formatRubKpi(microunits: number | null | undefined): string {
  return formatMoneyKpi(microunits, "RUB")
}

export function formatUsdKpi(microcents: number | null | undefined): string {
  return formatMoneyKpi(microcents, "USD")
}

/**
 * Изменение к предыдущему окну: доля прироста (0.124 = +12.4%).
 * null — сравнивать не с чем (нет окна или в прошлом окне ноль).
 */
export function deltaRatio(current: number, prev: number | null | undefined): number | null {
  if (prev == null || prev <= 0) return null
  return (current - prev) / prev
}

export function formatDeltaPercent(ratio: number, digits = 1): string {
  const pct = ratio * 100
  const sign = pct > 0 ? "+" : ""
  return `${sign}${pct.toFixed(digits)}%`
}

export function formatCompactNumber(value: number | null | undefined, locale = "en"): string {
  if (value == null) return "—"
  try {
    return new Intl.NumberFormat(locale === "ru" ? "ru-RU" : "en-US", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value)
  } catch {
    return String(value)
  }
}

export function formatInteger(value: number | null | undefined, locale = "en"): string {
  if (value == null) return "—"
  try {
    return new Intl.NumberFormat(locale === "ru" ? "ru-RU" : "en-US").format(value)
  } catch {
    return String(value)
  }
}

export function formatPercent(rate: number | null | undefined, digits = 1): string {
  if (rate == null) return "—"
  return `${(rate * 100).toFixed(digits)}%`
}

export function formatDurationSec(seconds: number | null | undefined, locale = "en"): string {
  if (seconds == null || seconds <= 0) return "0s"
  if (seconds < 60) return `${formatInteger(seconds, locale)}s`
  const mins = Math.floor(seconds / 60)
  const rem = seconds % 60
  if (mins < 60) return rem > 0 ? `${mins}m ${rem}s` : `${mins}m`
  const hours = Math.floor(mins / 60)
  const remMins = mins % 60
  return remMins > 0 ? `${hours}h ${remMins}m` : `${hours}h`
}

/** Primary unit for a single row: tokens → chars → duration. */
export function formatUsageUnits(row: UsageUnitsInput, locale = "en"): string {
  const tokens = row.total_tokens ?? 0
  const chars = row.spoken_text_length ?? 0
  const duration = row.billed_duration_sec ?? 0
  if (tokens > 0) return `${formatCompactNumber(tokens, locale)} tok`
  if (chars > 0) return `${formatCompactNumber(chars, locale)} chars`
  if (duration > 0) return formatDurationSec(duration, locale)
  return "—"
}

/** Подписи видов единиц; по умолчанию — латинские сокращения, UI передаёт локализованные. */
export type UsageUnitLabels = {
  tokens: string
  video: string
  audio: string
  chars: string
  images: string
}

export const DEFAULT_UNIT_LABELS: UsageUnitLabels = {
  tokens: "tok",
  video: "video",
  audio: "audio",
  chars: "chars",
  images: "img",
}

/**
 * Проданные (тарифные) единицы строки — только то, чем строка биллится:
 * секунды видео и аудио раздельно, штуки изображений, символы voice, токены
 * text. Токены-метрики провайдера у видео/картинок сюда не попадают — это
 * себестоимостная телеметрия, а не проданный объём. Ничего не суммируем и не
 * сокращаем: каждый ненулевой вид — отдельный элемент.
 */
export function billedUnitsPartsList(
  row: UsageUnitsInput,
  locale = "en",
  labels: UsageUnitLabels = DEFAULT_UNIT_LABELS,
): string[] {
  const parts: string[] = []
  const videoDur = row.video_duration_sec ?? 0
  const audioDur = row.audio_duration_sec ?? 0
  const genericDur = row.billed_duration_sec ?? 0
  const images = row.image_count ?? 0
  const voiceChars = row.voice_chars ?? 0
  const textTokens = row.text_tokens ?? 0
  if (videoDur > 0) parts.push(`${formatDurationSec(videoDur, locale)} ${labels.video}`)
  if (audioDur > 0) parts.push(`${formatDurationSec(audioDur, locale)} ${labels.audio}`)
  // Старые данные без сплита по медиа: показываем общие секунды, лишь бы не потерять.
  if (videoDur === 0 && audioDur === 0 && genericDur > 0) parts.push(formatDurationSec(genericDur, locale))
  if (images > 0) parts.push(`${formatCompactNumber(images, locale)} ${labels.images}`)
  if (voiceChars > 0) parts.push(`${formatCompactNumber(voiceChars, locale)} ${labels.chars}`)
  if (textTokens > 0) parts.push(`${formatCompactNumber(textTokens, locale)} ${labels.tokens}`)
  return parts
}

export function formatBilledUnits(
  row: UsageUnitsInput,
  locale = "en",
  labels: UsageUnitLabels = DEFAULT_UNIT_LABELS,
): string {
  const parts = billedUnitsPartsList(row, locale, labels)
  return parts.length > 0 ? parts.join(" · ") : "—"
}

/** Колонки «за что списано» вместо одной ячейки «Израсходовано». */
export const USAGE_KIND_COLUMNS = ["video", "image", "audio", "voice", "text"] as const
export type UsageKindColumn = (typeof USAGE_KIND_COLUMNS)[number]

/** video+lipsync → Видео; пустой media_kind / «—» / other колонки не имеют. */
export function mediaKindToUsageColumn(mediaKind: string | null | undefined): UsageKindColumn | null {
  switch (mediaKind) {
    case "video":
    case "lipsync":
      return "video"
    case "image":
      return "image"
    case "audio":
      return "audio"
    case "voice":
      return "voice"
    case "text":
      return "text"
    default:
      return null
  }
}

/**
 * Значение одной колонки вида. Ноль и пустое — прочерк, не «0s»: в раскрытой
 * строке чужие виды должны выглядеть пустыми, а не нулевыми.
 */
export function formatUsageKindColumn(
  column: UsageKindColumn,
  value: number | null | undefined,
  locale = "en",
  labels: UsageUnitLabels = DEFAULT_UNIT_LABELS,
): string {
  if (value == null || value <= 0) return "—"
  switch (column) {
    case "video":
    case "audio":
      return formatDurationSec(value, locale)
    case "image":
      return formatCompactNumber(value, locale)
    case "voice":
      return `${formatCompactNumber(value, locale)} ${labels.chars}`
    case "text":
      return `${formatCompactNumber(value, locale)} ${labels.tokens}`
  }
}

/**
 * Единицы строки «по медиа»: показываем ту единицу, которой этот вид медиа
 * тарифицируется (video/lipsync/audio — секунды, voice — символы, text — токены,
 * image — штуки), а не самую крупную цифру строки.
 */
export function formatMediaKindUnits(
  mediaKind: string,
  row: UsageUnitsInput,
  locale = "en",
  labels: UsageUnitLabels = DEFAULT_UNIT_LABELS,
): string {
  switch (mediaKind) {
    case "video":
    case "lipsync":
      return formatDurationSec((row.video_duration_sec ?? 0) || (row.billed_duration_sec ?? 0), locale)
    case "audio":
      return formatDurationSec((row.audio_duration_sec ?? 0) || (row.billed_duration_sec ?? 0), locale)
    case "voice":
      return `${formatCompactNumber((row.voice_chars ?? 0) || (row.spoken_text_length ?? 0), locale)} ${labels.chars}`
    case "text":
      return `${formatCompactNumber((row.text_tokens ?? 0) || (row.total_tokens ?? 0), locale)} ${labels.tokens}`
    case "image":
      return `${formatCompactNumber(row.image_count ?? 0, locale)} ${labels.images}`
    default:
      return formatBilledUnits(row, locale, labels)
  }
}

export function formatPeriodRange(
  from: string | null | undefined,
  to: string | null | undefined,
  locale = "en",
): string {
  if (!from && !to) return ""
  const loc = locale === "ru" ? "ru-RU" : "en-US"
  const fmt = (iso: string) => {
    try {
      return new Date(`${iso}T00:00:00`).toLocaleDateString(loc, { day: "2-digit", month: "short", year: "numeric" })
    } catch {
      return iso
    }
  }
  if (from && to && from === to) return fmt(from)
  if (from && to) return `${fmt(from)} – ${fmt(to)}`
  return fmt(from || to || "")
}

export function seriesMetricValue(
  point: { cost_microcents: number; revenue_microcents?: number; total_tokens: number; count: number },
  metric: "revenue" | "cost" | "tokens" | "count",
): number {
  if (metric === "revenue") return (point.revenue_microcents ?? 0) / MICROUNITS_PER_CURRENCY
  if (metric === "tokens") return point.total_tokens
  if (metric === "count") return point.count
  return point.cost_microcents / MICROCENTS_PER_USD
}

/** Format a calendar ISO date (YYYY-MM-DD) with the active UI locale, timezone-safe. */
export function formatCalendarDate(iso: string | null | undefined, locale = "en"): string {
  if (!iso) return "—"
  const [year, month, day] = iso.slice(0, 10).split("-").map(Number)
  if (!year || !month || !day) return iso.slice(0, 10)
  const date = new Date(Date.UTC(year, month - 1, day))
  return new Intl.DateTimeFormat(locale === "ru" ? "ru-RU" : "en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(date)
}
